import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Mail, Phone, Building2, Plus, MoreVertical, Edit, Trash2, X } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { AdminInfo } from "@/types/superAdmin";
import { CreateAdminDialog } from "@/components/super-admin/CreateAdminDialog";
import { EditAdminDialog } from "@/components/super-admin/EditAdminDialog";
import { ManageTenantsDialog } from "@/components/super-admin/ManageTenantsDialog";
import { DeleteAdminDialog } from "@/components/super-admin/DeleteAdminDialog";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { getAdmins, createAdmin, updateAdmin, deleteAdmin, assignTenant, revokeTenant } from "@/services/edgeFunctionWrappers/adminsManagementWrapper";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type DialogMode = 'create' | 'edit' | 'manage' | 'delete' | null;

export default function SuperAdminAdmins() {
  const { t } = useTranslation("superadmin");
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminInfo | null>(null);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  // `getAdmins` lève désormais en cas d'échec (403, 500, erreur métier en 200) :
  // on récupère l'état d'erreur pour ne plus afficher « Aucun administrateur
  // trouvé » alors que la requête a simplement échoué.
  const { data: admins = [], isLoading, isError, error: adminsError } = useQuery({
    queryKey: ['super-admin-admins'],
    queryFn: getAdmins,
  });

  // Les mutations d'administration rejettent maintenant sur session expirée ou
  // droits insuffisants : `onSuccess` ne se déclenche plus que sur un vrai
  // succès. Ce helper garantit qu'un message d'échec reste affichable même si
  // l'erreur remontée n'a pas de `message`.
  const describeError = (error: unknown, fallback: string) =>
    (error as { message?: string })?.message || fallback;

  const createAdminMutation = useMutation({
    mutationFn: createAdmin,
    onSuccess: () => {
      toast({ title: t("admins.createdSuccess") });
      queryClient.invalidateQueries({ queryKey: ['super-admin-admins'] });
      setDialogMode(null);
    },
    onError: (error: any) => {
      toast({ 
        title: t("admins.createError"),
        description: describeError(error, t("admins.createErrorDesc")),
        variant: "destructive"
      });
    },
  });

  const updateAdminMutation = useMutation({
    // `EditAdminDialog` soumet `{ userId, firstName, lastName, phone }` :
    // `formData.adminId` valait `undefined`, donc l'identifiant n'était jamais
    // transmis (audit 3.3).
    mutationFn: async (formData: any) => updateAdmin(formData.userId, formData),
    onSuccess: () => {
      toast({ title: t("admins.updatedSuccess") });
      queryClient.invalidateQueries({ queryKey: ['super-admin-admins'] });
      setDialogMode(null);
    },
    onError: (error: any) => {
      toast({ 
        title: t("admins.updateError"),
        description: describeError(error, t("admins.updateErrorDesc")),
        variant: "destructive"
      });
    },
  });

  const assignTenantMutation = useMutation({
    mutationFn: async ({ userId, gymId }: { userId: string; gymId: string }) => 
      assignTenant(userId, gymId),
    onSuccess: () => {
      toast({ title: t("admins.tenantAssignedSuccess") });
      queryClient.invalidateQueries({ queryKey: ['super-admin-admins'] });
    },
    onError: (error: any) => {
      toast({ 
        title: t("admins.assignError"),
        description: describeError(error, t("admins.assignErrorDesc")),
        variant: "destructive"
      });
    },
  });

  const revokeTenantMutation = useMutation({
    mutationFn: async ({ userId, gymId }: { userId: string; gymId: string }) =>
      revokeTenant(userId, gymId),
    onSuccess: () => {
      toast({ title: t("admins.revokedSuccess") });
      queryClient.invalidateQueries({ queryKey: ['super-admin-admins'] });
    },
    onError: (error: any) => {
      toast({ 
        title: t("admins.revokeError"),
        description: describeError(error, t("admins.revokeErrorDesc")),
        variant: "destructive"
      });
    },
  });

  const deleteAdminMutation = useMutation({
    // `deleteAccount` était déstructuré puis jamais transmis : la case
    // « supprimer aussi le compte » n'avait aucun effet (audit 3.3).
    mutationFn: async ({ userId, deleteAccount }: { userId: string; deleteAccount: boolean }) =>
      deleteAdmin(userId, deleteAccount),
    onSuccess: () => {
      toast({ title: t("admins.deletedSuccess") });
      queryClient.invalidateQueries({ queryKey: ['super-admin-admins'] });
      setDialogMode(null);
      setSelectedAdmin(null);
    },
    onError: (error: any) => {
      toast({ 
        title: t("admins.deleteError"),
        description: describeError(error, t("admins.deleteErrorDesc")),
        variant: "destructive"
      });
    },
  });

  const filteredAdmins = admins.filter((admin) =>
    `${admin.first_name} ${admin.last_name} ${admin.email} ${admin.tenants.map(t => t.gym_name).join(' ')}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const openDialog = (mode: DialogMode, admin?: AdminInfo) => {
    setDialogMode(mode);
    setSelectedAdmin(admin || null);
  };

  return (
    <SuperAdminLayout>
      <div className="space-y-4 md:space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:justify-between md:items-center">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">{t("admins.title")}</h1>
            <p className="text-sm md:text-base text-muted-foreground">{t("admins.subtitle")}</p>
          </div>
          <Button 
            onClick={() => openDialog('create')} 
            size={isMobile ? "sm" : "lg"}
            className="w-full md:w-auto"
          >
            <Plus className="h-4 w-4 mr-2" />
            {isMobile ? t("admins.create") : t("admins.createFull")}
          </Button>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-2.5 md:top-3 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("admins.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10"
          />
        </div>

        {isLoading ? (
          <Card>
            <CardContent className="p-6">
              <p className="text-muted-foreground">{t("admins.loading")}</p>
            </CardContent>
          </Card>
        ) : isError ? (
          // Un chargement en échec n'est pas une liste vide : on le dit.
          <Card>
            <CardContent className="p-6 space-y-3">
              <p className="text-destructive font-medium">
                {t("admins.loadError")}
              </p>
              <p className="text-sm text-muted-foreground">
                {describeError(adminsError, t("admins.errorOccurred"))}
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['super-admin-admins'] })}
              >
                {t("admins.retry")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filteredAdmins.map((admin) => (
              <Card key={admin.user_id} className="hover:bg-accent transition-colors">
                <CardHeader className="p-3 md:p-6">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-foreground text-base md:text-lg">
                      {admin.first_name} {admin.last_name}
                    </CardTitle>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon-sm" className="h-9 w-9">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openDialog('edit', admin)}>
                          <Edit className="h-4 w-4 mr-2" />
                          {t("admins.editAction")}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openDialog('manage', admin)}>
                          <Building2 className="h-4 w-4 mr-2" />
                          {t("admins.manageTenants")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => openDialog('delete', admin)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t("admins.deleteAction")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 p-3 md:p-6 pt-0">
                  <div className="flex flex-wrap gap-1 overflow-x-auto pb-1">
                    {admin.tenants.map((tenant) => (
                      <Badge 
                        key={tenant.gym_id} 
                        variant="secondary"
                        className="cursor-pointer hover:bg-secondary/80 text-xs whitespace-nowrap"
                      >
                        {tenant.gym_name}
                        <X 
                          className="h-3 w-3 ml-1 hover:text-destructive" 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (admin.tenants.length > 1) {
                              revokeTenantMutation.mutate({ userId: admin.user_id, gymId: tenant.gym_id });
                            } else {
                              toast({ 
                                title: t("admins.cannotRevoke"),
                                description: t("admins.cannotRevokeDesc"),
                                variant: "destructive"
                              });
                            }
                          }}
                        />
                      </Badge>
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate">{admin.email}</span>
                  </div>
                  
                  {admin.phone && (
                    <div className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                      <Phone className="h-4 w-4 flex-shrink-0" />
                      <span>{admin.phone}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!isLoading && !isError && filteredAdmins.length === 0 && (
          <Card>
            <CardContent className="p-6 text-center">
              <p className="text-muted-foreground">{t("admins.noneFound")}</p>
            </CardContent>
          </Card>
        )}
      </div>

      <CreateAdminDialog
        open={dialogMode === 'create'}
        onOpenChange={(open) => !open && setDialogMode(null)}
        onSubmit={createAdminMutation.mutate}
        isLoading={createAdminMutation.isPending}
      />

      <EditAdminDialog
        open={dialogMode === 'edit'}
        onOpenChange={(open) => !open && setDialogMode(null)}
        admin={selectedAdmin}
        onSubmit={updateAdminMutation.mutate}
        isLoading={updateAdminMutation.isPending}
      />

      <ManageTenantsDialog
        open={dialogMode === 'manage'}
        onOpenChange={(open) => !open && setDialogMode(null)}
        admin={selectedAdmin}
        onAssign={(userId, gymId) => assignTenantMutation.mutate({ userId, gymId })}
        onRevoke={(userId, gymId) => revokeTenantMutation.mutate({ userId, gymId })}
      />

      <DeleteAdminDialog
        open={dialogMode === 'delete'}
        onOpenChange={(open) => !open && setDialogMode(null)}
        admin={selectedAdmin}
        onConfirm={(userId, deleteAccount) => deleteAdminMutation.mutate({ userId, deleteAccount })}
        isLoading={deleteAdminMutation.isPending}
      />
    </SuperAdminLayout>
  );
}
